import { useTranslation } from 'react-i18next';

import { Box, Button, LightBulbOutline, Menu, MenuButton, MenuDivider, MenuItem, MenuList, StarSolid, Text, ViewGridOutline } from '@bonusly/design-system';

import type { PromptLibraryPrompt } from '../lib/prompt-library';

interface BizyPromptLibraryMenuProps {
	favoritePrompts: PromptLibraryPrompt[];
	onSelectPrompt: (prompt: PromptLibraryPrompt, promptText: string) => void;
	onBrowsePrompts: () => void;
	onOpenMenu: () => void;
	promptName?: string;
	isDisabled?: boolean;
}

function PromptMenuItem({
	prompt,
	promptName,
	onSelectPrompt,
}: Pick<BizyPromptLibraryMenuProps, 'promptName' | 'onSelectPrompt'> & { prompt: PromptLibraryPrompt }) {
	const { t } = useTranslation('admin_bizy');
	const promptText = t(prompt.key, { name: promptName });

	return (
		<MenuItem _focus={{ bg: 'transparent' }} onClick={() => onSelectPrompt(prompt, promptText)}>
			<Text noOfLines={1}>{promptText}</Text>
		</MenuItem>
	);
}

export function BizyPromptLibraryMenu({ favoritePrompts, onSelectPrompt, onBrowsePrompts, onOpenMenu, promptName, isDisabled }: BizyPromptLibraryMenuProps) {
	const { t } = useTranslation('admin_bizy');

	return (
		<Menu placement="top-start" autoSelect={false} onOpen={onOpenMenu}>
			<MenuButton
				as={Button}
				size="xs"
				variant="ghost"
				leftIcon={<LightBulbOutline boxSize={3.5} />}
				bg="secondary.bgAlpha"
				borderRadius="6px"
				px={1.5}
				py={0.5}
				h="auto"
				minH="24px"
				color="text.soft"
				fontWeight="normal"
				isDisabled={isDisabled}
				_hover={{ bg: 'secondary.bgAlphaHover', color: 'text.default' }}
			>
				{t('promptLibrary.menuButton')}
			</MenuButton>
			<MenuList minW="280px" maxW="340px" bg="bg.surface" borderColor="border.soft" p={2}>
				<Box px={3} py={1}>
					<Text fontSize="xs" color="text.soft" fontWeight="semibold">
						<StarSolid boxSize={3} /> {t('promptLibrary.menuFavorites')}
					</Text>
				</Box>
				{favoritePrompts.map((prompt) => (
					<PromptMenuItem key={`favorite-${prompt.id}`} prompt={prompt} promptName={promptName} onSelectPrompt={onSelectPrompt} />
				))}
				<MenuDivider />
				<MenuItem icon={<ViewGridOutline boxSize={4} />} _focus={{ bg: 'transparent' }} onClick={onBrowsePrompts}>
					{t('promptLibrary.browsePrompts')}
				</MenuItem>
			</MenuList>
		</Menu>
	);
}
